'use client';

import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import type { FoilMaskConfig, GoonDeckFoilPresetConfig } from '@/lib/goondeck-foil-presets';
import { DEFAULT_LAYOUT_CORNER_PERCENT, FINE_FACET_MESH_URL, grainTextureUrl, hexToRgbTriple, layerEffectRecipe, layoutMaskUrl, maskShapeStyle, patternUrl } from '@/lib/goondeck-foil-render';

type MaskTarget = 'pattern' | 'art' | 'layout' | 'edge';

// Fixed per-target salt so "random" motion gives each region its own
// independent position instead of all four jumping to the same spot on
// every pointer move, while still only needing one shared random seed.
const MASK_TARGET_SALT: Record<MaskTarget, number> = { pattern: 1.7, art: 3.1, layout: 5.3, edge: 7.9 };

// Cheap deterministic pseudo-random in [0,1) from two numbers — not
// cryptographic, just needs to look scattered when the seed changes.
function pseudoRandom01(seed: number, salt: number): number {
  const v = Math.sin(seed * 12.9898 + salt * 78.233) * 43758.5453;
  return v - Math.floor(v);
}

// Picture-frame ring masks — used so the layout/edge layer effects only
// paint a frame around the card instead of the whole face. Layout = border
// ring + any info/stat areas laid over the face (everything but the art and
// the outer edge line) so a border and an info panel can share one effect.
// Built from `border-radius` + `padding` + a content-box/border-box mask
// XOR'd together (not a sharp-cornered polygon clip-path) so the ring's
// inner edge is rounded to match the card's actual corner radius, not just
// the outer edge — the frame follows the card shape "inside and out".
function ringMaskStyle(radius: string, thicknessPercent: number): CSSProperties {
  return {
    borderRadius: radius,
    padding: `${thicknessPercent}%`,
    boxSizing: 'border-box',
    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
  };
}

export interface FoilPresetOverlayProps {
  config: GoonDeckFoilPresetConfig;
  glareX?: number;
  glareY?: number;
  strong?: boolean;
  /** Card's own corner radius (e.g. the grid tile's `rounded-2xl` vs the
   * inspect modal's `rounded-[1.6rem]`) — the legacy CSS ring fallback (used
   * for old presets with layoutPattern "none") matches this exactly. */
  radius?: string;
  /** The card's actual border-radius expressed as a percent of its own
   * rendered width — e.g. a 16px radius on a 280px-wide card is ~5.7%. This
   * is what the vector layout-frame masks use to line their corners up with
   * the card's real rounded corner (a fixed-px CSS radius isn't a fixed %
   * of width across differently-sized card renders, so each call site
   * should pass its own best estimate rather than relying on one guess). */
  cornerPercent?: number;
}

/** Renders a card's foil surface from an admin-editable preset — replaces
 * the old hardcoded FOIL_STYLES/FOIL_BURST_PALETTES system. Same visual
 * technique validated earlier (facet-mesh mask + conic-gradient color +
 * tilt-driven reveal + hue-rotate), just every number/color/blend now comes
 * from `config` instead of being baked into this file. */
export function FoilPresetOverlay({ config, glareX = 50, glareY = 50, strong = false, radius = '1rem', cornerPercent = DEFAULT_LAYOUT_CORNER_PERCENT }: FoilPresetOverlayProps) {
  const { tunes } = config;
  const shiftX = (glareX - 50) * 1.6;
  const shiftY = (glareY - 50) * 1.2;

  // "Random" mask motion re-rolls to a new position every time the cursor
  // actually moves (glareX/glareY change), rather than tracking it at all —
  // that's what reads as artificial sparkle instead of a predictable sweep.
  // One shared seed, re-derived per target via MASK_TARGET_SALT below, so
  // this only needs a single bit of state regardless of how many regions
  // are set to "random".
  const [randomSeed, setRandomSeed] = useState(0);
  useEffect(() => {
    setRandomSeed(Math.random());
  }, [glareX, glareY]);
  // Pattern-specific shift, separate from the general glare shift above —
  // `patternGrip` is how hard the facet/dot/star/rain texture "sticks" to the
  // card's tilt (mask panning + the two color sweeps), so it reads as a
  // texture physically etched into the surface rather than a flat sticker
  // that just brightens/dims in place.
  const patternShiftX = shiftX * tunes.patternGrip;
  const patternShiftY = shiftY * tunes.patternGrip;

  const tiltDistance = Math.hypot(glareX - 50, glareY - 50);
  // Smoothstep, not linear — a straight ramp feels like it "snaps" once you
  // cross the distance threshold. `fadeWidth` (admin-tunable) controls how
  // far you have to tilt before it's fully revealed; bigger = gentler.
  const tiltLinear = Math.min(1, tiltDistance / (32 * tunes.fadeWidth));
  const tiltMagnitude = tiltLinear * tiltLinear * (3 - 2 * tiltLinear);
  const reveal = tunes.revealFloor + tiltMagnitude * tunes.revealStrength;
  const tiltAngle = Math.atan2(glareY - 50, glareX - 50) * (180 / Math.PI);
  const shineAngle = 110 + (glareX - 50) * 0.28;
  const glareXInv = 100 - glareX;
  const glareYInv = 100 - glareY;

  // Memoized — grainSize !== 1 rebuilds the SVG (a real feTurbulence filter,
  // not free), and this component re-renders on every pointer move for the
  // tilt effect, so this must not recompute unless the inputs actually change.
  const mesh = useMemo(
    () => patternUrl(config.pattern, config.grainType, tunes.grainSize, tunes.glistenSize),
    [config.pattern, config.grainType, tunes.grainSize, tunes.glistenSize]
  );
  const hasPattern = config.pattern !== 'none';
  const cursorGlowRgb = useMemo(() => hexToRgbTriple(config.cursorGlowColor), [config.cursorGlowColor]);
  const grainStrength = tunes.grainStrength;
  const grainTexture = useMemo(
    () => grainTextureUrl(config.grainType, tunes.grainSize),
    [config.grainType, tunes.grainSize]
  );
  const grainScale = config.pattern === 'grain' ? grainStrength : 1;
  const glistenScale = config.pattern === 'glisten' ? tunes.glistenStrength : 1;

  const baseOpacity = (strong ? 0.24 : 0.14) * tunes.base;
  // Glisten's specks are individually tiny/faint/blurred by design, so its
  // strength tune scales BOTH the masked color-reveal-through-specks pass
  // AND the texture-overlay pass below — turning it up needs to meaningfully
  // brighten the whole effect, not just nudge one of two layers.
  const primaryOpacity = (strong ? 0.58 : 0.46) * tunes.color * reveal * glistenScale;
  const secondaryOpacity = (strong ? 0.36 : 0.28) * tunes.color * reveal;
  const facetOpacity = (strong ? 0.48 : 0.34) * tunes.pattern * grainScale * glistenScale;
  const shineOpacity = (strong ? 0.46 : 0.3) * tunes.shine * reveal;
  const sweepOpacity = (strong ? 0.4 : 0.26) * tunes.sweep * reveal;
  // Was 0.075/0.045 — an order of magnitude fainter than every sibling
  // texture layer (shine/sweep/diagonal all sit in the 0.2-0.46 range), so
  // at the default grainStrength (1) it was effectively invisible. Brought
  // in line with those instead of leaving admins needing to crank the
  // strength tune just to see grain exist at all.
  const grainOpacity = (strong ? 0.34 : 0.22) * tunes.pattern * grainStrength;
  const diagonalOpacity = (strong ? 0.34 : 0.2) * tunes.shine * reveal;
  const sparkleOpacity = (strong ? 0.32 : 0.14) * tunes.sparkle;
  const topOpacity = strong ? 0.55 : 0.4;

  const cssVars = {
    '--hp-glare-x': `${glareX}%`,
    '--hp-glare-y': `${glareY}%`,
    '--hp-tilt-angle': `${tiltAngle}deg`,
    '--hp-shine-angle': `${shineAngle}deg`,
    // Mirrored across the card center — used by the "glassSweep" layer
    // effect so its glint sits opposite the cursor instead of following it.
    '--hp-glare-x-inv': `${100 - glareX}%`,
    '--hp-glare-y-inv': `${100 - glareY}%`,
    '--hp-shine-angle-inv': `${shineAngle + 180}deg`,
  } as CSSProperties;

  const patternEffect = layerEffectRecipe(config.layerEffects.pattern, config.colorA, config.colorB, config.grainType, config.glowColor);
  const art = layerEffectRecipe(config.layerEffects.art, config.colorA, config.colorB, config.grainType, config.glowColor);
  const layout = layerEffectRecipe(config.layerEffects.layout, config.colorA, config.colorB, config.grainType, config.glowColor);
  const edge = layerEffectRecipe(config.layerEffects.edge, config.colorA, config.colorB, config.grainType, config.glowColor);
  // Glow/specular are pure-white brightness effects with no saturated color
  // to lean on, so they get their own dedicated strength multipliers on top
  // of grain's existing one — otherwise they read far weaker than every
  // colored effect at the same nominal opacity.
  const layerStrength = (effect: string) => {
    if (effect === 'grain') return grainStrength;
    if (effect === 'glow') return tunes.glowStrength;
    if (effect === 'specular') return tunes.specularStrength;
    return 1;
  };

  // Wraps an already-rendered effect div in an extra masked layer per its
  // mask config — "mirror" motion centers the mask at the opposite point
  // from your cursor, "reverseMirror" centers it at your actual cursor
  // (matching glow/specular's own positioning), "random" re-rolls per
  // target off the shared seed above. Returns the node unchanged when the
  // target's mask shape is "none", so this is a no-op for every existing
  // preset until someone opts in.
  //
  // IMPORTANT: `angle` is always the real shineAngle here, never inverted.
  // For the directional shapes (glint/glare sweep), the mask is a linear
  // gradient — and rotating a symmetric band's angle by 180° while ALSO
  // mirroring its center position cancels out exactly (they land on the
  // same physical line), which is why an earlier version of this made
  // mirror and reverse-mirror look identical for glint-shaped masks. Only
  // the position should flip; the angle representing "which way the band
  // runs" shouldn't.
  const wrapWithShapeMask = (target: MaskTarget, node: ReactNode): ReactNode => {
    const maskCfg: FoilMaskConfig = config.masks[target];
    if (!maskCfg || maskCfg.shape === 'none') return node;
    const salt = MASK_TARGET_SALT[target];
    let posX = glareXInv;
    let posY = glareYInv;
    let angle = shineAngle;
    if (maskCfg.motion === 'random') {
      posX = pseudoRandom01(randomSeed, salt) * 100;
      posY = pseudoRandom01(randomSeed, salt + 0.37) * 100;
      angle = pseudoRandom01(randomSeed, salt + 0.71) * 360;
    } else if (maskCfg.motion === 'reverseMirror') {
      posX = glareX;
      posY = glareY;
    }
    return (
      <div className="absolute inset-0 overflow-hidden" style={maskShapeStyle(maskCfg.shape, posX, posY, angle, tunes.maskSize)}>
        {node}
      </div>
    );
  };

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" style={{ ...cssVars, opacity: tunes.overlayOpacity }}>
      {/* Base tint */}
      <div
        className="absolute inset-0"
        style={{
          background: `linear-gradient(135deg, ${config.baseColors[0]}, ${config.baseColors[1]}, ${config.baseColors[2]})`,
          mixBlendMode: 'screen',
          opacity: baseOpacity,
        }}
      />

      {/* Color, masked to the pattern so it only shows through open facets/dots/stars/rain */}
      {hasPattern && mesh ? (
        <div
          className="absolute inset-0 overflow-hidden transition-[mask-position,-webkit-mask-position] duration-75"
          style={{
            WebkitMaskImage: mesh,
            maskImage: mesh,
            WebkitMaskSize: '132% 132%',
            maskSize: '132% 132%',
            WebkitMaskRepeat: 'no-repeat',
            maskRepeat: 'no-repeat',
            WebkitMaskPosition: `${50 - patternShiftX * 0.5}% ${50 - patternShiftY * 0.5}%`,
            maskPosition: `${50 - patternShiftX * 0.5}% ${50 - patternShiftY * 0.5}%`,
          }}
        >
          {/* The "Pattern" mask shape used to only gate the OPTIONAL
              gold/iridescent/etc. recipe below, so leaving that on "none"
              (the default) made the mask shape control do nothing at all —
              wrapping the plain colorA/colorB sweep too means the mask
              actually restricts the pattern's visibility regardless of
              whether an extra recipe effect is assigned. */}
          {wrapWithShapeMask('pattern', (
            <>
              <div
                className="absolute inset-[-25%] transition-transform duration-75"
                style={{
                  background: `conic-gradient(from ${tiltAngle}deg at ${glareX}% ${glareY}%, ${config.colorA}, ${config.colorB}, ${config.colorA})`,
                  mixBlendMode: 'screen',
                  opacity: primaryOpacity,
                  transform: `translate(${patternShiftX * 0.6}px, ${patternShiftY * 0.6}px)`,
                  filter: `hue-rotate(${tiltAngle}deg) saturate(1.6) blur(0.5px)`,
                }}
              />
              {/* Optional pattern-effect recipe (gold/iridescent/etc.)
                  layered on top of the plain sweep — lets a pattern
                  actually read as "gold" or "iridescent" instead of only
                  ever the flat colorA/colorB sweep. */}
              {config.layerEffects.pattern !== 'none' && (
                <div
                  className="absolute inset-[-25%] transition-transform duration-75"
                  style={{
                    background: patternEffect.background,
                    mixBlendMode: patternEffect.blend,
                    filter: patternEffect.filter,
                    opacity: patternEffect.opacity * reveal * layerStrength(config.layerEffects.pattern) * tunes.patternEffectOpacity,
                    transform: `translate(${patternShiftX * 0.6}px, ${patternShiftY * 0.6}px)`,
                  }}
                />
              )}
            </>
          ))}
        </div>
      ) : (
        // No pattern — plain smooth color wash (e.g. Standard/no-foil tier).
        <div
          className="absolute inset-[-25%] transition-transform duration-75"
          style={{
            background: `conic-gradient(from ${tiltAngle}deg at ${glareX}% ${glareY}%, ${config.colorA}, ${config.colorB}, ${config.colorA})`,
            mixBlendMode: 'screen',
            opacity: primaryOpacity * 0.6,
            transform: `translate(${shiftX * 0.6}px, ${shiftY * 0.6}px)`,
            filter: `hue-rotate(${tiltAngle}deg) blur(0.5px)`,
          }}
        />
      )}

      {/* Smaller offset facet layer — a second, finer triangle mesh shifted
          away from the main mask with its color order flipped, so a
          scattering of small triangles catch the *inverse* tone instead of
          just repeating the big facets' color. This is what actually reads
          as "glistening" rather than one flat colored mask. Facet patterns
          only — dots/stars/rain/grain don't have a matching finer variant. */}
      {hasPattern && (config.pattern === 'facet' || config.pattern === 'facet2' || config.pattern === 'fineFacet') && wrapWithShapeMask('pattern', (
        <div
          className="absolute inset-0 overflow-hidden transition-[mask-position,-webkit-mask-position] duration-75"
          style={{
            WebkitMaskImage: FINE_FACET_MESH_URL,
            maskImage: FINE_FACET_MESH_URL,
            WebkitMaskSize: '132% 132%',
            maskSize: '132% 132%',
            WebkitMaskRepeat: 'no-repeat',
            maskRepeat: 'no-repeat',
            // Offset from the main mask's position so the small triangles'
            // open spots land somewhere different than the big ones.
            WebkitMaskPosition: `${58 - patternShiftX * 0.5}% ${42 - patternShiftY * 0.5}%`,
            maskPosition: `${58 - patternShiftX * 0.5}% ${42 - patternShiftY * 0.5}%`,
          }}
        >
          <div
            className="absolute inset-[-25%] transition-transform duration-75"
            style={{
              // Color order flipped from the main sweep (colorB first) —
              // the inverse tone of whatever the big triangles are showing.
              background: `conic-gradient(from ${tiltAngle + 130}deg at ${glareX}% ${glareY}%, ${config.colorB}, ${config.colorA}, ${config.colorB})`,
              mixBlendMode: 'screen',
              opacity: secondaryOpacity,
              transform: `translate(${patternShiftX * -0.4}px, ${patternShiftY * -0.4}px)`,
              filter: `hue-rotate(${tiltAngle + 180}deg) saturate(1.6) blur(0.5px)`,
            }}
          />
        </div>
      ))}

      {/* Pattern visible as texture too, not just a mask. This used to
          always render the mesh's own literal white shapes via `overlay`
          blend no matter what — so even with a "Gold"/"Iridescent"/etc.
          pattern effect assigned, this unconditional white wash sat on top
          and fought it, reading as washed-out/whitish instead of the
          effect's actual color. When an effect is assigned, mask a div
          filled with THAT effect's own background/blend instead of the
          plain white mesh. */}
      {hasPattern && mesh && wrapWithShapeMask('pattern', (
        config.layerEffects.pattern !== 'none' ? (
          <div
            className="absolute inset-[-6%] overflow-hidden transition-transform duration-75"
            style={{
              WebkitMaskImage: mesh,
              maskImage: mesh,
              WebkitMaskSize: '100% 100%',
              maskSize: '100% 100%',
              transform: `translate(${patternShiftX * 0.25}px, ${patternShiftY * 0.25}px)`,
            }}
          >
            <div
              className="absolute inset-0"
              style={{
                background: patternEffect.background,
                mixBlendMode: patternEffect.blend,
                filter: patternEffect.filter,
                opacity: facetOpacity * layerStrength(config.layerEffects.pattern),
              }}
            />
          </div>
        ) : (
          <div
            className="absolute inset-[-6%] transition-transform duration-75"
            style={{
              backgroundImage: mesh,
              backgroundSize: '100% 100%',
              mixBlendMode: 'overlay',
              opacity: facetOpacity,
              transform: `translate(${patternShiftX * 0.25}px, ${patternShiftY * 0.25}px)`,
            }}
          />
        )
      ))}

      {/* Shine / glare */}
      <div
        className="absolute inset-0"
        style={{
          background: `radial-gradient(circle at ${glareX}% ${glareY}%, rgba(${cursorGlowRgb},.92), rgba(${cursorGlowRgb},.28) 12%, transparent 31%), linear-gradient(${shineAngle}deg, transparent 19%, rgba(${cursorGlowRgb},.1) 34%, rgba(${cursorGlowRgb},.72) 47%, transparent 76%)`,
          mixBlendMode: 'screen',
          opacity: shineOpacity,
        }}
      />

      {/* Sweep — spread controls how wide/feathered the band is (bigger =
          softer, more spread out; smaller = tight, sharp streak), separate
          from opacity which just controls how strong it looks. */}
      <div
        className="absolute inset-[-18%] transition-transform duration-75"
        style={{
          background: `linear-gradient(${shineAngle}deg, transparent ${Math.max(0, 45 - 39 * tunes.sweepSpread)}%, rgba(255,255,255,.04) ${Math.max(0, 45 - 18 * tunes.sweepSpread)}%, rgba(255,255,255,.62) 45%, transparent ${Math.min(100, 45 + 30 * tunes.sweepSpread)}%), repeating-linear-gradient(115deg, transparent 0 ${18 * tunes.sweepSpread}px, rgba(255,255,255,.11) ${19 * tunes.sweepSpread}px ${20 * tunes.sweepSpread}px, transparent ${21 * tunes.sweepSpread}px ${42 * tunes.sweepSpread}px)`,
          mixBlendMode: 'screen',
          opacity: sweepOpacity,
          transform: `translate(${shiftX}px, ${shiftY}px)`,
          // Thin repeating-line textures alias hard under the card's 3D
          // perspective tilt (shows up as moving moiré/banding) — a touch of
          // blur softens the fine lines enough to kill it without losing the
          // sweep look.
          filter: 'saturate(1.3) contrast(1.08) blur(0.6px)',
        }}
      />

      {/* Grain */}
      {hasPattern && (
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: grainTexture,
            backgroundSize: '150px 150px',
            mixBlendMode: 'overlay',
            opacity: grainOpacity,
          }}
        />
      )}

      {/* Diagonal sheen */}
      {hasPattern && (
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(${shineAngle}deg, transparent 28%, rgba(255,255,255,.25) 45%, rgba(255,255,255,.05) 53%, transparent 71%)`,
            mixBlendMode: 'overlay',
            opacity: diagonalOpacity,
          }}
        />
      )}

      {/* Sparkles (fixed, not glare-linked) */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(circle, rgba(255,255,255,.86) 0 1px, transparent 1.55px) 2px 7px / 38px 38px, linear-gradient(70deg, transparent 0 48%, rgba(255,255,255,.42) 49% 50%, transparent 51% 100%) 12px 18px / 64px 64px',
          mixBlendMode: 'screen',
          opacity: sparkleOpacity,
        }}
      />

      {/* Per-region layer effects */}
      {config.layerEffects.art !== 'none' && wrapWithShapeMask('art', (
        <div
          className="absolute inset-0"
          style={{ background: art.background, mixBlendMode: art.blend, filter: art.filter, opacity: art.opacity * reveal * layerStrength(config.layerEffects.art) * tunes.artEffectOpacity }}
        />
      ))}
      {config.layerEffects.layout !== 'none' && wrapWithShapeMask('layout', (() => {
        const layoutMask = layoutMaskUrl(config.layoutPattern, cornerPercent);
        return (
          <div
            className="absolute inset-0"
            style={
              layoutMask
                ? {
                    background: layout.background,
                    mixBlendMode: layout.blend,
                    filter: layout.filter,
                    opacity: layout.opacity * reveal * layerStrength(config.layerEffects.layout) * tunes.layoutEffectOpacity,
                    WebkitMaskImage: layoutMask,
                    maskImage: layoutMask,
                    WebkitMaskSize: '100% 100%',
                    maskSize: '100% 100%',
                    WebkitMaskRepeat: 'no-repeat',
                    maskRepeat: 'no-repeat',
                  }
                : {
                    // Old presets with no layoutPattern set — plain ring,
                    // same as before, for backward compatibility.
                    background: layout.background,
                    mixBlendMode: layout.blend,
                    filter: layout.filter,
                    opacity: layout.opacity * reveal * layerStrength(config.layerEffects.layout) * tunes.layoutEffectOpacity,
                    ...ringMaskStyle(radius, 6),
                  }
            }
          />
        );
      })())}
      {config.layerEffects.edge !== 'none' && wrapWithShapeMask('edge', (
        <div
          className="absolute inset-0"
          style={{
            background: edge.background,
            mixBlendMode: edge.blend,
            filter: edge.filter,
            opacity: edge.opacity * reveal * tunes.edge * layerStrength(config.layerEffects.edge),
            ...ringMaskStyle(radius, 2),
          }}
        />
      ))}

      {/* Full-card continuity pass. This is not an edge-only ring; it gently
          bridges the foil across the whole surface and only softens at the
          physical card edge, preventing the effect from reading as a square
          pasted on top of the art. */}
      {tunes.edgeFeather > 0 && hasPattern && (
        <div
          className="absolute inset-0"
          style={{
            background: `radial-gradient(circle at ${glareX}% ${glareY}%, rgba(255,255,255,.36), transparent 34%), linear-gradient(${shineAngle}deg, transparent 0 16%, rgba(255,255,255,.14) 35%, transparent 62%), conic-gradient(from ${tiltAngle}deg at ${glareX}% ${glareY}%, ${config.colorA}, rgba(255,255,120,.52), ${config.colorB}, rgba(80,255,160,.46), ${config.colorA})`,
            maskImage: 'radial-gradient(ellipse at center, black 0 72%, rgba(0,0,0,.9) 84%, rgba(0,0,0,.48) 96%, transparent 103%)',
            WebkitMaskImage: 'radial-gradient(ellipse at center, black 0 72%, rgba(0,0,0,.9) 84%, rgba(0,0,0,.48) 96%, transparent 103%)',
            mixBlendMode: 'screen',
            opacity: (0.08 + reveal * 0.24) * tunes.edgeFeather,
            filter: 'blur(1.2px) saturate(2.1) contrast(1.12)',
          }}
        />
      )}

      {/* Always-on top glare — base surface gloss, not reveal-scaled. Wide,
          soft falloff (0% to 55%+) on purpose — a tight radius reads as a
          hard-edged hotspot snapping in as the cursor crosses it. Easy to
          mistake for the "Glow" layer effect since both are a white
          cursor-following highlight, but this one is unconditional and NOT
          affected by Glow's mask/motion/strength settings — topGlareOpacity
          is the only way to turn it down if it's muddying a Glow effect
          you're trying to isolate and tune. */}
      <div
        className="absolute inset-0"
        style={{
          background: `radial-gradient(circle at ${glareX}% ${glareY}%, rgba(${cursorGlowRgb},${strong ? 0.3 : 0.18}) 0%, rgba(${cursorGlowRgb},${strong ? 0.12 : 0.07}) 30%, transparent ${strong ? 55 : 62}%), linear-gradient(115deg, transparent 0%, rgba(${cursorGlowRgb},${strong ? 0.14 : 0.08}) 42%, transparent 52%)`,
          mixBlendMode: 'screen',
          opacity: topOpacity * tunes.topGlareOpacity,
        }}
      />
    </div>
  );
}
